import {
  type AuroraClientType,
  type AuroraPushAction,
  auroraSetContextApi,
  auroraSetSupportedToolsApi,
  type ClientDispatchedToolArgs,
  type ClientDispatchedToolId,
  type ClientDispatchedToolResult,
  clientDispatchedTools
} from 'api-validations/aurora'
import type { PanoramaWebSocketApi } from 'api-validations/websocket'
import { commaSeparatedStrings } from 'api-validations/zod-extra'
import type React from 'react'
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { path } from 'utils'

import { useOrgCodeOrThrow } from '../active-operation'
import {
  type QueryKey,
  type UseInfiniteQueryResult,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryResult,
  useApiClient,
  useInfiniteQuery,
  useMutation,
  useQuery
} from '../api-provider'
import { useEnabledFeatureFlags } from '../debug'
import { useLocalization } from '../localization'
import {
  type AuroraContextSlots,
  activeSupportedTools,
  auroraRegistration,
  useAuroraRegistrationStore
} from './auroraRegistrationStore'
import { AuroraClient, type DispatchOutcome } from './AuroraWebsocketClient'

interface AuroraContext {
  client: AuroraClient | null
  isConnected: boolean
  /**
   * URL of Aurora's MCP tool server, for the WebRTC realtime path. Not
   * derived from `wsUrl`: OpenAI's own servers call this directly (not this
   * app), so — unlike the WebSocket connection — it has to be genuinely
   * internet-reachable even when developing locally, which is why this is
   * its own independently-configured value rather than a `ws→http`
   * transform of the (possibly `localhost`) WebSocket URL.
   */
  mcpUrl: string
}

const AuroraContext = createContext<AuroraContext>({
  client: null,
  isConnected: false,
  mcpUrl: ''
})

/**
 * Build a microtask-coalesced sender for a piece of store-derived state.
 * The returned `fire` function coalesces multiple synchronous calls in
 * the same tick into one deferred read+send, so writes that fan in from
 * multiple components in the same render commit ship a single
 * fully-settled snapshot rather than partial intermediates.
 *
 * Optional `dedup: true` adds serialize-equality dedup against the last
 * successfully-sent value. Use it when the read returns a *derived* value
 * that can stay equal across upstream changes (e.g. an active-tools list
 * computed from a ref-counted map — a count going 2 → 1 changes the
 * underlying state but not the derived list). For values that are
 * already deduped at the setter (see `setContextSlot`), leave this off;
 * the setter's dedup means the subscriber won't fire on no-op writes
 * anyway, and adding dedup here would be redundant work.
 */
const makeStoreSender = <T,>(args: {
  read: () => T
  send: (value: T) => Promise<unknown>
  failureLabel: string
  dedup?: boolean
}): (() => void) => {
  let lastSerialized: string | undefined
  let pending = false
  return () => {
    if (pending) return
    pending = true
    queueMicrotask(() => {
      pending = false
      const value = args.read()
      if (args.dedup === true) {
        const serialized = JSON.stringify(value)
        if (serialized === lastSerialized) return
        lastSerialized = serialized
      }
      args.send(value).catch((error) => {
        console.warn(`Aurora: failed to send ${args.failureLabel}`, error)
        if (args.dedup === true) lastSerialized = undefined
      })
    })
  }
}

export const AuroraContextProvider: React.FC<{
  /**
   * Full WebSocket URL for the Aurora endpoint, with no query string.
   * Typically `wss://aurora.${domain}` in deployed environments;
   * `ws://localhost:8080` when pointing at a local `dev:server`.
   */
  wsUrl: string
  /**
   * URL of Aurora's MCP tool server (the WebRTC realtime path's tool calls),
   * called by OpenAI's own servers rather than this app. See the
   * AuroraContext `mcpUrl` field for why this isn't derived from `wsUrl`.
   */
  mcpUrl: string
  clientType: AuroraClientType.Web | AuroraClientType.Mobile
  /**
   * The commit this client bundle was built from. Aurora hands it to tools that
   * read client source, so they describe the build in front of this user rather
   * than whatever is on `main`. Each client supplies its own because the value
   * is injected at build time and the two clients build separately.
   */
  commitSha?: string
  children: React.ReactNode
}> = ({ wsUrl, mcpUrl, clientType, commitSha, children }) => {
  const orgCode = useOrgCodeOrThrow()
  const { locale, measureSystem } = useLocalization()
  const apiClient = useApiClient()
  const [isConnected, setIsConnected] = useState(false)
  // Encoded here so the client memo below sees a stable primitive; toggling a
  // flag reconnects, the same as switching operations does.
  const enabledFeatureFlags = commaSeparatedStrings.encode(useEnabledFeatureFlags())

  const client = useMemo(
    () =>
      new AuroraClient({
        name: 'Aurora',
        path: ({ access_token }) =>
          path(wsUrl, {
            orgCode,
            locale,
            measureSystem,
            access_token,
            authorization_type: 'access_token',
            clientType,
            commitSha,
            enabledFeatureFlags,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }),
        getAccessToken: () => apiClient.getAccessToken()
      }),
    [orgCode, locale, measureSystem, wsUrl, apiClient, clientType, commitSha, enabledFeatureFlags]
  )

  useEffect(() => {
    client.setOnStatusChange((connected) => {
      setIsConnected(connected)
      if (!connected) {
        client.rejectAllPending('WebSocket disconnected')
      }
    })
    client.connect()
    return () => client.disconnect()
  }, [client])

  // Push the merged context-slot map to Aurora whenever it changes. On
  // reconnect, an initial send syncs whatever the store holds.
  useEffect(() => {
    if (!isConnected) return
    const fire = makeStoreSender({
      read: () => useAuroraRegistrationStore.getState().contextSlots,
      send: (slots) => client.send(auroraSetContextApi, { body: { context: slots } }),
      failureLabel: 'SetContext'
    })
    const unsubscribe = useAuroraRegistrationStore.subscribe((s) => s.contextSlots, fire)
    fire()
    return unsubscribe
  }, [client, isConnected])

  // Push the merged supported-tools declaration whenever its ref-counted
  // membership changes. Dedup is on because the active-tools list is
  // derived (counts > 0, sorted) and can be unchanged even when the
  // underlying counter map churns. Re-fires on reconnect so the server's
  // fresh per-session state always sees what handlers the client has
  // registered.
  useEffect(() => {
    if (!isConnected) return
    const fire = makeStoreSender({
      read: () => activeSupportedTools(useAuroraRegistrationStore.getState().supportedTools),
      send: (tools) => client.send(auroraSetSupportedToolsApi, { body: { supportedTools: tools } }),
      failureLabel: 'SetSupportedTools',
      dedup: true
    })
    const unsubscribe = useAuroraRegistrationStore.subscribe((s) => s.supportedTools, fire)
    fire()
    return unsubscribe
  }, [client, isConnected])

  return (
    <AuroraContext.Provider value={{ client, isConnected, mcpUrl }}>
      {children}
    </AuroraContext.Provider>
  )
}

export const useAuroraClient = (): AuroraClient | null => useContext(AuroraContext).client

export const useAuroraMcpUrl = (): string => useContext(AuroraContext).mcpUrl

/** Whether the WebSocket to Aurora is open; it reconnects on its own after a drop. */
export const useAuroraIsConnected = (): boolean => useContext(AuroraContext).isConnected

export const auroraQueryKey = (apiDef: PanoramaWebSocketApi.AnyDefinition): QueryKey => [
  'aurora-websocket',
  apiDef
]

export const useAuroraQuery = <ApiDef extends PanoramaWebSocketApi.AnyDefinition>(
  apiDef: ApiDef,
  body: Omit<PanoramaWebSocketApi.RequestPayload<ApiDef>, 'action'>,
  options?: {
    enabled?: boolean
    /**
     * Poll while the answer is provisional. Some routes answer immediately with
     * work-in-progress rather than holding the request open — a summary whose
     * generation runs for minutes cannot be awaited inside a request — so the
     * caller decides from the payload whether to ask again, and how soon.
     *
     * Not consulted once a fetch has settled into error; polling stops there.
     */
    refetchInterval?: (
      data: PanoramaWebSocketApi.TerminalPayload<ApiDef> | undefined
    ) => number | false
  }
): UseQueryResult<PanoramaWebSocketApi.TerminalPayload<ApiDef>, Error> => {
  const { client, isConnected } = useContext(AuroraContext)
  const { locale } = useLocalization()
  const orgCode = useOrgCodeOrThrow()
  const { refetchInterval } = options ?? {}

  type TerminalData = PanoramaWebSocketApi.TerminalPayload<ApiDef>

  return useQuery<TerminalData, Error, TerminalData, QueryKey>({
    queryKey: [...auroraQueryKey(apiDef), orgCode, body, locale],
    enabled: isConnected && options?.enabled !== false,
    // A settled error stops the poll: `retry` bounds attempts within one fetch,
    // and the interval would otherwise hand it a fresh one indefinitely. The
    // last successful payload survives an error, so a caller reading only the
    // data would keep asking for a stream whose every request is failing.
    // Recovery is the refetch on window focus.
    refetchInterval:
      refetchInterval === undefined
        ? false
        : (query) => (query.state.status === 'error' ? false : refetchInterval(query.state.data)),
    queryFn: ({ signal }) => {
      if (client === null) {
        throw new Error('Aurora client not available')
      }
      return client.send(apiDef, { body, signal })
    }
  })
}

export const useAuroraInfiniteQuery = <ApiDef extends PanoramaWebSocketApi.AnyDefinition, Item>(
  apiDef: ApiDef,
  body: Omit<PanoramaWebSocketApi.RequestPayload<ApiDef>, 'action' | 'continuationToken'>,
  options: {
    selectPage: (page: PanoramaWebSocketApi.TerminalPayload<ApiDef>) => Item[]
    getNextPageParam: (page: PanoramaWebSocketApi.TerminalPayload<ApiDef>) => string | undefined
    enabled?: boolean
    maxPages?: number
  }
): UseInfiniteQueryResult<Item[], Error> => {
  const { client, isConnected } = useContext(AuroraContext)
  const { locale } = useLocalization()
  const orgCode = useOrgCodeOrThrow()

  return useInfiniteQuery<
    PanoramaWebSocketApi.TerminalPayload<ApiDef>,
    Error,
    Item[],
    QueryKey,
    string | undefined
  >({
    queryKey: [...auroraQueryKey(apiDef), orgCode, body, locale],
    enabled: isConnected && options.enabled !== false,
    initialPageParam: undefined,
    maxPages: options.maxPages,
    queryFn: ({ signal, pageParam }) => {
      if (client === null) {
        throw new Error('Aurora client not available')
      }
      return client.send(apiDef, { body: { ...body, continuationToken: pageParam }, signal })
    },
    getNextPageParam: options.getNextPageParam,
    select: (data) => data.pages.flatMap(options.selectPage)
  })
}

/**
 * Contribute a typed slice of Aurora client context. Components anywhere in
 * the AuroraContextProvider subtree can populate independent slots (e.g.
 * `currentlyViewing`) and the provider merges them into a single SetContext
 * payload pushed to the cloud.
 *
 * Passing `undefined` (or unmounting) removes the slot from the merged
 * payload. Multiple components contributing to the same slot is a contract
 * violation — the last writer wins, and unmounts will clear what other
 * writers are still maintaining. Keep slot ownership to one component
 * per slot.
 */
export const useAuroraContextSlot = <K extends keyof AuroraContextSlots>(
  key: K,
  value: AuroraContextSlots[K]
): void => {
  useEffect(() => {
    auroraRegistration.setContextSlot(key, value)
  }, [key, value])

  // Separate effect so value transitions don't trigger a clear→set blip;
  // the slot is only cleared when the component genuinely unmounts (or its
  // key prop changes, which shouldn't happen in practice since keys are
  // usually string literals).
  useEffect(
    () => () => {
      auroraRegistration.setContextSlot(key, undefined)
    },
    [key]
  )
}

export const useAuroraMutation = <ApiDef extends PanoramaWebSocketApi.AnyDefinition>(
  apiDef: ApiDef,
  options?: Omit<
    UseMutationOptions<
      PanoramaWebSocketApi.TerminalPayload<ApiDef>,
      Error,
      Omit<PanoramaWebSocketApi.RequestPayload<ApiDef>, 'action'>
    >,
    'mutationFn'
  > & {
    onIntermediate?: (payload: PanoramaWebSocketApi.IntermediatePayload<ApiDef>) => void
  }
): UseMutationResult<
  PanoramaWebSocketApi.TerminalPayload<ApiDef>,
  Error,
  Omit<PanoramaWebSocketApi.RequestPayload<ApiDef>, 'action'>
> => {
  const { client } = useContext(AuroraContext)
  const { onIntermediate, ...mutationOptions } = options ?? {}

  return useMutation({
    ...mutationOptions,
    mutationFn: (body) => {
      if (client === null) {
        throw new Error('Aurora client not available')
      }
      return client.send(apiDef, { body, onIntermediate })
    }
  })
}

/**
 * Caller-supplied handler for a single client-dispatched tool. The hook
 * pre-parses incoming args against the tool's argsSchema, so handlers
 * receive a typed value rather than `unknown`. The hook also runtime-
 * validates the return against the tool's resultSchema and replies with
 * `client_error: invalid_result` if it doesn't match.
 */
export type AuroraDispatchHandler<Id extends ClientDispatchedToolId> = (
  args: ClientDispatchedToolArgs<Id>
) => ClientDispatchedToolResult<Id> | Promise<ClientDispatchedToolResult<Id>>

export type AuroraDispatchHandlerMap = {
  [Id in ClientDispatchedToolId]?: AuroraDispatchHandler<Id>
}

/**
 * Push action handlers. The wire schema's push envelope currently defines
 * only `AgentNotification`; this map grows naturally as new actions are
 * added without protocol changes.
 */
export type AuroraPushHandlerMap = {
  AgentNotification?: (data: { text: string }) => void
}

type UseAuroraIncomingHandlersInput = {
  dispatch?: AuroraDispatchHandlerMap
  push?: AuroraPushHandlerMap
}

/**
 * Register handlers for server-initiated messages. The hook:
 *   1. Installs dispatch + push handlers on the AuroraClient.
 *   2. Registers the dispatch handler keys with the supported-tools store
 *      slot, so the provider's SetSupportedTools sender picks them up.
 *      Ref-counted: multiple components can register the same toolId and
 *      the declaration survives until all of them unmount.
 *
 * Handler *identities* may change across renders (inline arrows are fine);
 * registration is keyed on the *set* of handler names, so as long as the
 * shape of `input` is stable across renders, we don't re-register.
 */
export const useAuroraIncomingHandlers = (input: UseAuroraIncomingHandlersInput): void => {
  const client = useAuroraClient()

  const dispatchKeys = useMemo(
    () => Object.keys(input.dispatch ?? {}).sort() as ClientDispatchedToolId[],
    [input.dispatch]
  )
  const pushKeys = useMemo(
    () => Object.keys(input.push ?? {}).sort() as AuroraPushAction[],
    [input.push]
  )

  // Serialized join so the effect's dep list only fires when the *set* of
  // handler names changes — not on every render with new handler refs.
  const dispatchKeysFp = dispatchKeys.join(',')
  const pushKeysFp = pushKeys.join(',')

  // Refs let the registered closure read the latest handler implementations
  // without forcing a re-register every render.
  const inputRef = useRef(input)
  useEffect(() => {
    inputRef.current = input
  })

  // Register handlers on the AuroraClient.
  useEffect(() => {
    if (client === null) return

    const unregisterFns: (() => void)[] = []

    for (const toolId of dispatchKeys) {
      const tool = clientDispatchedTools[toolId]
      const unregister = client.setDispatchHandler(
        toolId,
        async (rawArgs): Promise<DispatchOutcome> => {
          const argsParse = tool.argsSchema.safeParse(rawArgs)
          if (!argsParse.success) {
            return {
              status: 'client_error',
              code: 'invalid_args',
              message: `Args failed schema validation for ${toolId}: ${argsParse.error.message}`
            }
          }

          const handler = inputRef.current.dispatch?.[toolId]
          if (handler === undefined) {
            return {
              status: 'client_error',
              code: 'unsupported_tool',
              message: `Handler for ${toolId} was removed before invocation`
            }
          }

          const result = await (handler as AuroraDispatchHandler<typeof toolId>)(argsParse.data)

          const resultParse = tool.resultSchema.safeParse(result)
          if (!resultParse.success) {
            return {
              status: 'client_error',
              code: 'invalid_result',
              message: `Handler return failed schema validation for ${toolId}: ${resultParse.error.message}`
            }
          }

          return { status: 'success', value: resultParse.data }
        }
      )
      unregisterFns.push(unregister)
    }

    for (const action of pushKeys) {
      const unregister = client.setPushHandler(action, (rawData) => {
        const handler = inputRef.current.push?.[action as keyof AuroraPushHandlerMap]
        if (handler === undefined) return
        // The push envelope was already schema-validated by AuroraClient
        // before routing, so the cast lands on a parsed value.
        handler(rawData as Parameters<NonNullable<typeof handler>>[0])
      })
      unregisterFns.push(unregister)
    }

    return () => {
      for (const unregister of unregisterFns) unregister()
    }
  }, [client, dispatchKeysFp, pushKeysFp])

  // Register tool presence in the store. The provider's sender effect
  // observes the store and emits SetSupportedTools whenever the merged
  // set changes. Decoupled from the connection lifecycle here — the
  // provider handles reconnect re-syncs on its own.
  useEffect(() => {
    const unregisterFns = dispatchKeys.map(auroraRegistration.registerSupportedTool)
    return () => {
      for (const unregister of unregisterFns) unregister()
    }
  }, [dispatchKeysFp])
}
